import React, { useEffect, useState } from "react";
import backarrow from "../../Assets/svg/backarrow.svg";
import dt from "../../Assets/svg/delete.svg";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Container, Row, Col } from "react-bootstrap";
import MultiSelectDropDown from "../common/searchbox/multi-select-dropdown.js";
import SearchWithAPI from "../common/searchbox/searchbox.js";
import { apiCall, apiCallAdmin } from "../../services/api-service.js";

const EMPTY_VALIDATIONS = {
  code: "",
  name: "",
  email: "",
  phonenumber: "",
  projects: "",
  city_id: "",
  state_id: "",
  country_id: "",
};

const validateEmail = (email) => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};

const Clientadd = () => {
  const apiUrl = process.env.REACT_APP_API_URL;
  const adminUrl = process.env.REACT_APP_ADMIN_API_URL;
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const id = location.state?.id || searchParams.get("id");
  const navigate = useNavigate();

  const [selectedProjectOptions, setSelectedProjectOptions] = useState([]);
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [emails, setEmails] = useState([{ name: "", email: "", primary: true }]);

  const handleEmailChange = (index, field, value) => {
    setEmails((prev) => prev.map((item, idx) => (idx === index ? { ...item, [field]: value } : item)));
  };

  // Primary is a radio group: exactly one email is primary at a time.
  const setPrimaryEmail = (index) => {
    setEmails((prev) => prev.map((item, idx) => ({ ...item, primary: idx === index })));
  };

  const addEmailField = () => {
    setEmails((prev) => [...prev, { name: "", email: "", primary: false }]);
  };

  const removeEmailField = (index) => {
    setEmails((prev) => {
      if (prev.length === 1) return [{ name: "", email: "", primary: true }];
      const next = prev.filter((_, i) => i !== index);
      // If we removed the primary, make the first remaining email primary.
      if (!next.some((e) => e.primary)) next[0] = { ...next[0], primary: true };
      return next;
    });
  };
  const [phoneNumber, setPhoneNumber] = useState("");
  const [address, setAddress] = useState("");
  const [zipcode, setZipCode] = useState("");
  const [cityId, setCityId] = useState(null);
  const [stateId, setStateId] = useState(null);
  const [countryId, setCountryId] = useState(null);
  const [validations, setValidations] = useState(EMPTY_VALIDATIONS);
  const [loading, setLoading] = useState(false);

  const handleProjectDropdownChange = (selectedOption) => {
    // Compare by value, not object reference: in edit mode the pre-selected
    // options come from the API as different object instances than the dropdown's
    // options, so reference equality would never match (and fail to deselect).
    setSelectedProjectOptions((prev) =>
      prev.some((o) => o.value === selectedOption.value)
        ? prev.filter((o) => o.value !== selectedOption.value)
        : [...prev, selectedOption]
    );
  };

  const handleCityDropdownChange = (option) => {
    setCityId(option);
    if (option) {
      setStateId(option.state_id ? { value: option.state_id, label: option.state_name || option.state || "" } : null);
      setCountryId(option.country_id ? { value: option.country_id, label: option.country_name || option.country || "" } : null);
    } else {
      setStateId(null);
      setCountryId(null);
    }
  };
  const handleStateDropdownChange = (option) => {
    setStateId(option);
    if (option) {
      setCountryId(option.country_id ? { value: option.country_id, label: option.country_name || option.country || "" } : null);
    } else {
      setCountryId(null);
    }
  };
  const handleCountryDropdownChange = (option) => {
    setCountryId(option);
  };

  const clientSave = async () => {
    if (loading) return;
    setLoading(true);
    try {
      setValidations(EMPTY_VALIDATIONS);

      // Validate email format and duplicates, and phone format all at once.
      let emailError = "";
      const hasInvalidEmail = emails.some(
        (item) => item.email.trim() !== "" && !validateEmail(item.email.trim())
      );
      if (hasInvalidEmail) {
        emailError = "Please enter a valid email address";
      }

      const emailList = emails.map(item => item.email.trim().toLowerCase()).filter(email => email !== "");
      const hasDuplicates = emailList.some((email, index) => emailList.indexOf(email) !== index);
      if (hasDuplicates && !emailError) {
        emailError = "Duplicate email addresses are not allowed";
      }

      let phoneError = "";
      if (phoneNumber) {
        const phoneTrimmed = phoneNumber.trim();
        const hasLetters = /[A-Za-z]/.test(phoneTrimmed);
        const isValidFormat = /^\+?[0-9\s\-()]{7,15}$/.test(phoneTrimmed);
        if (hasLetters || !isValidFormat) {
          phoneError = "Invalid phone number format";
        }
      }

      if (emailError || phoneError) {
        setValidations((prev) => ({
          ...prev,
          email: emailError,
          phonenumber: phoneError,
        }));
        return;
      }

      const emailValue = emails
        .filter((item) => item.email.trim() !== "")
        .map((item) => ({
          name: item.name.trim(),
          email: item.email.trim(),
          type: item.primary ? "Primary" : "Secondary",
        }));

      const requestBody = {
        code,
        name,
        email: emailValue,
        phonenumber: phoneNumber,
        projects: selectedProjectOptions.map((item) => item.value),
        address,
        city_id: cityId?.value || 0,
        state_id: stateId?.value || 0,
        country_id: countryId?.value || 0,
        zipcode,
      };

      const isNew = !id || id === -1;
      const response = await (isNew
        ? apiCall("post", "client", requestBody)
        : apiCall("put", `client/${id}`, requestBody));

      if (response.data?.success === true) {
        navigate(-1);
      } else if (response.data?.error) {
        const validationTemp = { ...EMPTY_VALIDATIONS };
        Object.entries(response.data.error).forEach(([key, value]) => {
          validationTemp[key] = Array.isArray(value) && value.length > 0 ? value[0] : "";
        });
        setValidations(validationTemp);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const clientEdit = async () => {
    const response = await apiCall("get", `client/${id}`);
    if (response.data?.success === true) {
      const { client } = response.data;
      const addr = client.billing_address || client.address_details || client || {};
      setCode(client.code ?? "");
      setName(client.name ?? "");

      const emailList = client.email_details || client.emails;
      if (Array.isArray(emailList) && emailList.length > 0) {
        const parsed = emailList.map((e) => ({
          name: e.name ?? "",
          email: e.email ?? "",
          primary: e.type ? e.type.toLowerCase() === "primary" : !!e.primary,
        }));
        if (!parsed.some((e) => e.primary)) parsed[0].primary = true;
        setEmails(parsed);
      } else if (client.email_address || client.email) {
        const rawEmail = client.email_address || client.email || "";
        const parts = rawEmail.split(",").map((p) => p.trim());
        const parsed = parts.map((part) => {
          const match = part.match(/(.+?)\s*\((Primary|Secondary)\)/i);
          const email = match ? match[1].trim() : part;
          const primary = match ? /primary/i.test(match[2]) : true;
          return { name: "", email, primary };
        });
        if (!parsed.some((e) => e.primary) && parsed.length > 0) parsed[0].primary = true;
        setEmails(parsed.length > 0 ? parsed : [{ name: "", email: "", primary: true }]);
      } else {
        setEmails([{ name: "", email: "", primary: true }]);
      }

      setPhoneNumber(client.phonenumber ?? "");

      const addressVal = addr.address ?? client.address ?? "";
      const zipVal = addr.zipcode ?? client.zipcode ?? "";
      const cityIdVal = addr.city_id ?? client.city_id;
      const cityNameVal = addr.city ?? client.city;
      const stateIdVal = addr.state_id ?? client.state_id;
      const stateNameVal = addr.state ?? client.state;
      const countryIdVal = addr.country_id ?? client.country_id;
      const countryNameVal = addr.country ?? client.country;

      setAddress(addressVal);
      setZipCode(zipVal);
      setCityId(cityIdVal ? { value: cityIdVal, label: cityNameVal || "" } : null);
      setStateId(stateIdVal ? { value: stateIdVal, label: stateNameVal || "" } : null);
      setCountryId(countryIdVal ? { value: countryIdVal, label: countryNameVal || "" } : null);
      setSelectedProjectOptions(client.projects ?? []);
    }
  };

  useEffect(() => {
    if (id != null && id !== -1) {
      clientEdit();
    }
  }, [id]);

  return (
    <Container fluid style={{ width: "100%", padding: "0px" }}>
      <div className="d-flex side3">
        <Row
          sm={12}
          style={{
            "--bs-gutter-x": "0px",
            margin: "0px",
            width: "100%",
            // height: "100vh",
            overflow: "auto",
            scrollbarWidth: "none",
            msOverflowStyle: "none",
            justifyContent: "center",
          }}
        >
          <Col>
            <div style={{ padding: "16px", fontFamily: "Satoshi" }}>
              <div
                style={{
                  paddingBottom: "16px",
                  gap: "10px",
                  backgroundColor: "#F3F6F9",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                  className="mb-3"
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: "12px",
                    }}
                  >
                    <Link to="/client/list">
                      <div style={{ borderRadius: "100px" }}>
                        <img src={backarrow} />
                      </div>
                    </Link>
                    <div>
                      <span
                        style={{
                          fontFamily: "Satoshi",
                          fontWeight: "700",
                          fontSize: "16px",
                          lineHeight: "21.6px",
                          color: "#212129",
                        }}
                      >
                        {id ? "Edit Client" : "New Client"}
                      </span>
                    </div>
                  </div>

                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: "12px",
                    }}
                  >
                    <div
                      onClick={() => {
                        navigate(-1);
                      }}
                      style={{
                        borderRadius: "100px",
                        border: "0.6px solid #E5E5E5",
                        padding: "10px 28px 10px 28px",
                        backgroundColor: "#FFFFFF",
                        cursor: "pointer",
                      }}
                    >
                      <span
                        style={{
                          fontFamily: "Satoshi",
                          fontWeight: "700",
                          fontSize: "14px",
                          lineHeight: "18.9px",
                          color: "#21272C",
                        }}
                      >
                        Cancel
                      </span>
                    </div>

                    <div
                      style={{
                        borderRadius: "100px",
                        border: "0.6px solid #E5E5E5",
                        padding: "10px 28px 10px 28px",
                        backgroundColor: loading ? "#A0B0FB" : "#3253EB",
                        cursor: loading ? "not-allowed" : "pointer",
                        fontFamily: "Satoshi",
                        fontWeight: "700",
                        fontSize: "14px",
                        lineHeight: "18.9px",
                        color: "#FFFFFF",
                        pointerEvents: loading ? "none" : "auto",
                      }}
                      onClick={clientSave}
                    >
                      {loading ? "Saving..." : (id ? "Update" : "Save")}
                    </div>
                  </div>
                </div>

                <div
                  className="px-4 py-3 d-flex flex-column"
                  style={{
                    borderRadius: "16px",
                    gap: "16px",
                    backgroundColor: "#FFFFFF",
                  }}
                >
                  <div
                    className="d-flex flex-column mb-3"
                    style={{ gap: "8px" }}
                  >
                    <div
                      style={{
                        fontSize: "20px",
                        fontWeight: "700",
                        color: "#212129",
                      }}
                    >
                      {id ? "Edit Client" : "Add Client"}
                    </div>
                    <div
                      style={{
                        fontSize: "14px",
                        fontWeight: "400",
                        color: "#8790A1",
                      }}
                    >
                      {id
                        ? "Enter below details to update client"
                        : "Enter below details to create new client"}
                    </div>
                  </div>

                  <div
                    className="d-flex flex-column mb-3 pb-3"
                    style={{ borderBottom: "1px dashed #E5E5E5" }}
                  >
                    {/* Row 1: Code | Client name */}
                    <Row className="gy-3">
                      <Col md={6} xxl={4}>
                        {/* Code field */}
                        <div
                          className="d-flex flex-column"
                          style={{ gap: "8px" }}
                        >
                          <div
                            style={{
                              fontSize: "13px",
                              fontWeight: "500",
                              color: "#212129",
                            }}
                          >
                            Code<span style={{ color: "red" }}> *</span>
                          </div>
                          <div
                            style={{ width: "100%" }}
                            className="d-flex flex-column"
                          >
                            <input
                              type="text"
                              placeholder="Enter code"
                              style={{
                                borderRadius: "10px",
                                border: "0.7px solid #E5E5E5",
                                backgroundColor: "#FFFFFF",
                                width: "100%",
                                fontWeight: "500",
                                fontSize: "14px",
                                color: "#212129",
                              }}
                              className="px-2 py-2"
                              value={code}
                              onChange={(event) => {
                                setCode(event.target.value);
                              }}
                            />
                            <div
                              style={{
                                fontSize: "12px",
                                fontWeight: "500",
                                color: "#FF4444",
                              }}
                              className="mt-1"
                            >
                              {validations.code}
                            </div>
                          </div>
                        </div>
                      </Col>

                      <Col md={6} xxl={4}>
                        {/* Client name field */}
                        <div
                          className="d-flex flex-column"
                          style={{ gap: "8px" }}
                        >
                          <div
                            style={{
                              fontSize: "13px",
                              fontWeight: "500",
                              color: "#212129",
                            }}
                          >
                            Client name<span style={{ color: "red" }}> *</span>
                          </div>
                          <div
                            style={{ width: "100%" }}
                            className="d-flex flex-column"
                          >
                            <input
                              type="text"
                              value={name}
                              onChange={(event) => {
                                setName(event.target.value);
                              }}
                              placeholder="Enter name"
                              style={{
                                borderRadius: "10px",
                                border: "0.7px solid #E5E5E5",
                                backgroundColor: "#FFFFFF",
                                width: "100%",
                                fontWeight: "500",
                                fontSize: "14px",
                                color: "#212129",
                              }}
                              className="px-2 py-2"
                            />
                            <div
                              style={{
                                fontSize: "12px",
                                fontWeight: "500",
                                color: "#FF4444",
                              }}
                              className="mt-1"
                            >
                              {validations.name}
                            </div>
                          </div>
                        </div>
                      </Col>
                    </Row>

                    {/* Row 2: Project | Mobile number */}
                    <Row className="gy-3 mt-0">
                      <Col md={6} xxl={4}>
                        {/* Project field */}
                        <div className="d-flex flex-column" style={{ gap: "8px" }}>
                          <div
                            style={{
                              fontSize: "13px",
                              fontWeight: "500",
                              color: "#212129",
                            }}
                          >
                            Project
                          </div>
                          <div style={{ width: "100%" }}>
                            <MultiSelectDropDown
                              onDropdownChange={handleProjectDropdownChange}
                              apiUrl={apiUrl + "get_project"}
                              placeholder="Projects"
                              selectedOption={selectedProjectOptions}
                            />
                          </div>
                        </div>
                      </Col>

                      <Col md={6} xxl={4}>
                        {/* Mobile number field */}
                        <div className="d-flex flex-column" style={{ gap: "8px" }}>
                          <div
                            style={{
                              fontSize: "13px",
                              fontWeight: "500",
                              color: "#212129",
                            }}
                          >
                            Mobile number
                          </div>
                          <div style={{ width: "100%" }} className="d-flex flex-column">
                            <input
                              type="text"
                              value={phoneNumber}
                              onChange={(event) => {
                                setPhoneNumber(event.target.value);
                              }}
                              placeholder="Enter mobile number"
                              style={{
                                borderRadius: "10px",
                                border: "0.7px solid #E5E5E5",
                                backgroundColor: "#FFFFFF",
                                width: "100%",
                                fontWeight: "500",
                                fontSize: "14px",
                                color: "#212129",
                              }}
                              className="px-2 py-2"
                            />
                            <div
                              style={{
                                fontSize: "12px",
                                fontWeight: "500",
                                color: "#FF4444",
                              }}
                              className="mt-1"
                            >
                              {validations.phonenumber}
                            </div>
                          </div>
                        </div>
                      </Col>
                    </Row>

                    {/* Emails (full width, below the fields above) */}
                    <div className="d-flex flex-column mt-3 col-12 col-lg-8 col-xxl-6" style={{ gap: "8px" }}>
                          <div
                            style={{
                              fontSize: "15px",
                              fontWeight: "700",
                              color: "#212129",
                            }}
                          >
                            Emails
                          </div>

                          {/* Column headers */}
                          <div className="d-flex align-items-center" style={{ gap: "8px" }}>
                            <div style={{ width: "200px", flexShrink: 0, fontSize: "13px", fontWeight: "500", color: "#212129" }}>
                              Name
                            </div>
                            <div style={{ flex: 1, fontSize: "13px", fontWeight: "500", color: "#212129" }}>
                              Email
                            </div>
                            {/* Spacer over the +/delete buttons */}
                            <div style={{ width: "72px", flexShrink: 0 }} />
                            <div style={{ width: "56px", flexShrink: 0, textAlign: "center", fontSize: "13px", fontWeight: "500", color: "#212129" }}>
                              Primary
                            </div>
                          </div>

                          <div className="d-flex flex-column" style={{ gap: "8px",  overflowY: "auto", paddingRight: "4px" }}>
                            {emails.map((item, index) => {
                              const isDuplicate =
                                item.email.trim() !== "" &&
                                emails.some((e, idx) => idx !== index && e.email.trim().toLowerCase() === item.email.trim().toLowerCase());
                              return (
                                <div key={index} className="d-flex align-items-center" style={{ gap: "8px" }}>
                                  {/* Name */}
                                  <input
                                    type="text"
                                    value={item.name}
                                    onChange={(event) => handleEmailChange(index, "name", event.target.value)}
                                    placeholder="Name"
                                    style={{
                                      width: "200px",
                                      flexShrink: 0,
                                      borderRadius: "10px",
                                      border: "0.7px solid #E5E5E5",
                                      backgroundColor: "#FFFFFF",
                                      fontWeight: "500",
                                      fontSize: "14px",
                                      color: "#212129",
                                      height: "40px",
                                    }}
                                    className="px-2 py-2"
                                  />
                                  {/* Email */}
                                  <input
                                    type="text"
                                    value={item.email}
                                    onChange={(event) => handleEmailChange(index, "email", event.target.value)}
                                    placeholder="Enter email address"
                                    style={{
                                      flex: 1,
                                      borderRadius: "10px",
                                      border: `0.7px solid ${isDuplicate ? "#FF4444" : "#E5E5E5"}`,
                                      backgroundColor: "#FFFFFF",
                                      fontWeight: "500",
                                      fontSize: "14px",
                                      color: "#212129",
                                      height: "40px",
                                    }}
                                    className="px-2 py-2"
                                  />
                                  {/* Add: only on the last row (placeholder keeps columns aligned) */}
                                  {index === emails.length - 1 ? (
                                    <button
                                      type="button"
                                      onClick={addEmailField}
                                      title="Add email"
                                      style={{
                                        width: "32px",
                                        height: "32px",
                                        flexShrink: 0,
                                        borderRadius: "8px",
                                        border: "1px solid #E5E5E5",
                                        backgroundColor: "#F4F0FF",
                                        color: "#6929E2",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        fontSize: "18px",
                                        fontWeight: "bold",
                                        cursor: "pointer",
                                        outline: "none",
                                      }}
                                    >
                                      +
                                    </button>
                                  ) : (
                                    <div style={{ width: "32px", flexShrink: 0 }} />
                                  )}
                                  {/* Delete: only when there is more than one row */}
                                  {emails.length > 1 ? (
                                    <button
                                      type="button"
                                      onClick={() => removeEmailField(index)}
                                      title="Remove email"
                                      style={{
                                        width: "32px",
                                        height: "32px",
                                        flexShrink: 0,
                                        borderRadius: "8px",
                                        border: "1px solid #FF4444",
                                        backgroundColor: "#FFF1F1",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        cursor: "pointer",
                                        outline: "none",
                                      }}
                                    >
                                      <img
                                        src={dt}
                                        alt="remove"
                                        style={{
                                          width: "14px",
                                          height: "14px",
                                          filter:
                                            "invert(36%) sepia(89%) saturate(2000%) hue-rotate(335deg) brightness(100%) contrast(105%)",
                                        }}
                                      />
                                    </button>
                                  ) : (
                                    <div style={{ width: "32px", flexShrink: 0 }} />
                                  )}
                                  {/* Primary radio */}
                                  <div style={{ width: "56px", flexShrink: 0, display: "flex", justifyContent: "center" }}>
                                    <input
                                      type="radio"
                                      name="primary-email"
                                      checked={!!item.primary}
                                      onChange={() => setPrimaryEmail(index)}
                                      style={{ width: "18px", height: "18px", accentColor: "#6929E2", cursor: "pointer" }}
                                    />
                                  </div>
                                </div>
                              );
                            })}
                          </div>

                          <div
                            style={{
                              fontSize: "12px",
                              fontWeight: "500",
                              color: "#FF4444",
                            }}
                            className="mt-1"
                          >
                            {validations.email}
                          </div>
                        </div>
                  </div>

                  <div>
                    <div
                      style={{
                        fontSize: "15px",
                        fontWeight: "700",
                        color: "#212129",
                      }}
                      className="mb-2"
                    >
                      Address info
                    </div>

                    <Row className="gy-3">
                      <Col md={6}>
                        <div className="d-flex flex-column" style={{ gap: "8px" }}>
                          <div
                            style={{
                              fontSize: "13px",
                              fontWeight: "500",
                              color: "#212129",
                            }}
                          >
                            Address
                          </div>
                          <div style={{ width: "100%" }} className="d-flex flex-column">
                            <input
                                                              type="text"
                                                              value={address}
                                                              onChange={(event) => {
                                                                setAddress(event.target.value);
                                                              }}
                                                              placeholder="Enter address"
                                                              style={{
                                                                borderRadius: "10px",
                                                                border: "0.7px solid #E5E5E5",
                                                                backgroundColor: "#FFFFFF",
                                                                width: "100%",
                                                                fontWeight: "500",
                                                                fontSize: "14px",
                                                                color: "#212129",
                                                              }}
                                                              className="px-2 py-2"
                                                            />
                            <div
                              style={{
                                fontSize: "12px",
                                fontWeight: "500",
                                color: "#FF4444",
                              }}
                              className="mt-1"
                            >
                              {validations.address}
                            </div>
                          </div>
                        </div>
                      </Col>

                      <Col md={6}>
                        <div className="d-flex flex-column" style={{ gap: "8px" }}>
                          <div
                            style={{
                              fontSize: "13px",
                              fontWeight: "500",
                              color: "#212129",
                            }}
                          >
                            City
                          </div>
                          <div style={{ width: "100%" }} className="d-flex flex-column">
                            <SearchWithAPI
                              onDropdownChange={handleCityDropdownChange}
                              apiUrl={adminUrl + "get_city"}
                              params={{}}
                              placeholder={"City"}
                              selectedValue={cityId != null ? cityId.label : ""}
                              selectedOption={cityId}
                            />
                            <div
                              style={{
                                fontSize: "12px",
                                fontWeight: "500",
                                color: "#FF4444",
                              }}
                              className="mt-1"
                            >
                              {validations.city_id}
                            </div>
                          </div>
                        </div>
                      </Col>

                      <Col md={6}>
                        <div className="d-flex flex-column" style={{ gap: "8px" }}>
                          <div
                            style={{
                              fontSize: "13px",
                              fontWeight: "500",
                              color: "#212129",
                            }}
                          >
                            State
                          </div>
                          <div style={{ width: "100%" }} className="d-flex flex-column">
                            <SearchWithAPI
                              onDropdownChange={handleStateDropdownChange}
                              apiUrl={adminUrl + "get_state"}
                              params={{}}
                              placeholder={"State"}
                              selectedValue={
                                stateId != null ? stateId.label : ""
                              }
                              selectedOption={stateId}
                            />
                            <div
                              style={{
                                fontSize: "12px",
                                fontWeight: "500",
                                color: "#FF4444",
                              }}
                              className="mt-1"
                            >
                              {validations.state_id}
                            </div>
                          </div>
                        </div>
                      </Col>

                      <Col md={6}>
                        <div className="d-flex flex-column" style={{ gap: "8px" }}>
                          <div
                            style={{
                              fontSize: "13px",
                              fontWeight: "500",
                              color: "#212129",
                            }}
                          >
                            Country
                          </div>
                          <div style={{ width: "100%" }} className="d-flex flex-column">
                            <SearchWithAPI
                              onDropdownChange={handleCountryDropdownChange}
                              apiUrl={adminUrl + "get_country"}
                              params={{}}
                              placeholder={"Country"}
                              selectedValue={
                                countryId != null ? countryId.label : ""
                              }
                              selectedOption={countryId}
                            />
                            <div
                              style={{
                                fontSize: "12px",
                                fontWeight: "500",
                                color: "#FF4444",
                              }}
                              className="mt-1"
                            >
                              {validations.country_id}
                            </div>
                          </div>
                        </div>
                      </Col>

                      <Col md={6}>
                        <div className="d-flex flex-column" style={{ gap: "8px" }}>
                          <div
                            style={{
                              fontSize: "13px",
                              fontWeight: "500",
                              color: "#212129",
                            }}
                          >
                            Zip code
                          </div>
                          <div style={{ width: "100%" }} className="d-flex flex-column">
                            <input
                              type="text"
                              value={zipcode}
                              onChange={(event) => {
                                setZipCode(event.target.value);
                              }}
                              placeholder="Enter zip code"
                              style={{
                                borderRadius: "10px",
                                border: "0.7px solid #E5E5E5",
                                backgroundColor: "#FFFFFF",
                                width: "100%",
                                fontWeight: "500",
                                fontSize: "14px",
                                color: "#212129",
                              }}
                              className="px-2 py-2"
                            />
                            <div
                              style={{
                                fontSize: "12px",
                                fontWeight: "500",
                                color: "#FF4444",
                              }}
                              className="mt-1"
                            >
                              {validations.zipcode}
                            </div>
                          </div>
                        </div>
                      </Col>
                    </Row>
                  </div>
                </div>
              </div>
            </div>
          </Col>
        </Row>
      </div>
    </Container>
  );
};

export default Clientadd;

// import backarrow from "../../Assets/svg/backarrow.svg";
// import Topbar from "../Topbar/Topbar";
// import Sidebar from "../Sidebar/Sidebar";
// import { Link } from "react-router-dom";
// import axios from "axios";
// import edit from "../../Assets/svg/edit.svg";
// import dt from "../../Assets/svg/delete.svg";
// import { Container, Row, Col, Toast, ToastContainer } from "react-bootstrap";
// import { getCookie } from "../getcookie.js";
// import { useNavigate } from "react-router-dom";
// import MultiSelectDropDown from "../common/searchbox/multi-select-dropdown.js";

// const Clientadd = ({ children }) => {
//     const apiUrl = process.env.REACT_APP_API_URL;
//     const adminUrl = process.env.REACT_APP_ADMIN_API_URL;

//     const [selectedProjectOptions, setSelectedProjectOptions] = useState([]);
//     const handleProjectDropdownChange = (selectedOptions) => {

//         console.log(selectedOptions);

//         const index = selectedProjectOptions.findIndex(option => option === selectedOptions);

//         console.log(index);
//         if (index > -1) {
//             const updatedOptions = selectedProjectOptions.filter(option => option !== selectedOptions);
//             setSelectedProjectOptions(updatedOptions);

//         } else {
//             setSelectedProjectOptions([...selectedProjectOptions, selectedOptions]);
//         }

//     };

//     const [projectList, setProjectList] = useState([]);
//     const [selectedProject, setSelectedProject] = useState("");
//     const [cityList, setCityList] = useState([]);
//     const [selectedCity, setSelectedCity] = useState("");
//     const [stateList, setStateList] = useState([]);
//     const [selectedState, setSelectedState] = useState("");
//     const [countryList, setCountryList] = useState([]);
//     const [selectedCountry, setSelectedCountry] = useState("");
//     const [clientNumber, setClientNumber] = useState("");
//     const [clientEmail, setClientEmail] = useState("");
//     const id = null;
//     const navigate = useNavigate();
//     const [codeValidate, setCodeValidate] = useState("");
//     const [nameValidate, setNameValidate] = useState("");
//     const [email, setEmail] = useState("");
//     const [code, setCode] = useState("");
//     const [address, setAddress] = useState("");
//     const [name, setName] = useState("");
//     const [phoneNumber, setPhoneNumber] = useState("");
//     const [cityId, setCityId] = useState(null);
//     const [stateId, setStateId] = useState(null);
//     const [countryId, setCountryId] = useState(null);

//     const handleCountry = (option) => {

//         setCountryId(option)
//     };

//     const handleState = (option) => {

//         setStateId(option)
//     };

//     const handleCity = (option) => {

//         setCityId(option)
//     };

//     const [validations, setValidations] = useState({
//         code: "",
//         name: "",
//         email: "",
//         phonenumber: "",
//         projects: "",
//         city_id: "",
//         state_id: "",
//         country_id: "",

//     });

//     const clientSave = async () => {
//         try {
//             setValidations({
//                 code: "",
//                 name: "",
//                 email: "",
//                 phonenumber: "",
//                 projects: "",
//                 city_id: "",
//                 state_id: "",
//                 country_id: "",
//             });

//             console.log('selectedCity');
//             console.log(selectedCity);
//             console.log('selectedState');
//             console.log(selectedState);
//             console.log('selectedCountry');
//             console.log(selectedCountry);

//             const requestBody = {
//                 code: code,
//                 name: name,
//                 email: email,
//                 phonenumber: phoneNumber,
//                 projects: selectedProjectOptions != null && selectedProjectOptions.map((item) => item.value),
//                 //projects: selectedProject ? selectedProject.value : '',
//                 city_id: selectedCity ? selectedCity : 0,
//                 state_id: selectedState ? selectedState : 0,
//                 country_id: selectedCountry ? selectedCountry : 0,

//             };

//             console.log('requestbody...', requestBody);

//             if (id === null || id === undefined || id === -1) {
//                 await axios.post(apiUrl + `client`, requestBody, {
//                     headers: {
//                         "Content-Type": "application/json",
//                         Accept: "application/json",
//                         "Access-Control-Allow-Origin": "*",
//                         Authorization: getCookie("mawalog"),
//                     },
//                 }).then((response) => {
//                     if (response.status === 400) {
//                     } else if (response.data.success === true) {
//                         navigate(-1);
//                     }
//                 }).catch((error) => {
//                     if (error.response) {
//                         console.error("Error Status:", error.response.status);
//                         console.error("Error Data:", error.response.data);

//                         const validationTemp = { ...validations };
//                         Object.keys(validationTemp).forEach((key) => {
//                             validationTemp[key] = "";
//                         });
//                         Object.entries(error.response.data.error).forEach(([key, value]) => {
//                             validationTemp[key] = value.length === 0 ? '' : value[0];
//                         });

//                         console.log("Helllloooo", validationTemp);
//                         setValidations(validationTemp);
//                     } else {
//                         console.error("Request failed:", error.message);
//                     }
//                 });
//             } else {
//                 await axios.put(apiUrl + `client/` + id, requestBody, {
//                     headers: {
//                         "Content-Type": "application/json",
//                         Accept: "application/json",
//                         "Access-Control-Allow-Origin": "*",
//                         Authorization: getCookie("mawalog"),
//                     },
//                 }).then((response) => {
//                     if (response.status === 400) {
//                         // Handle 400 response
//                     } else if (response.data.success === true) {
//                         navigate(-1);
//                     }
//                 }).catch((error) => {
//                     if (error.response) {
//                         console.error("Error Status:", error.response.status);
//                         console.error("Error Data:", error.response.data);

//                         if (error.response.status === 400) {
//                             // Handle 400 error
//                         }

//                         setValidations((validation) => {
//                             const validationTemp = { ...validation };
//                             Object.keys(validationTemp).forEach((key) => {
//                                 validationTemp[key] = "";
//                             });
//                             Object.entries(error.response.data.error).forEach(([key, value]) => {
//                                 validationTemp[key] = value.length === 0 ? '' : value[0];
//                             });

//                             return validationTemp;
//                         });
//                     } else {
//                         console.error("Request failed:", error.message);
//                     }
//                 });
//             }
//         } catch (error) {
//             console.log(error);
//             if (error.response && error.response.status === 400 && error.response.data.error) {
//                 setValidations(error.response.data.error);
//             }
//         }
//     };

//     const getProject = async () => {
//         try {
//             const response = await axios.get(
//                 apiUrl + `get_project`,
//                 {
//                     headers: {
//                         "Content-Type": "application/json; charset=UTF-8",
//                         Accept: "application/json",
//                         "Access-Control-Allow-Origin": "*",
//                         Authorization:
//                             getCookie("mawalog"),
//                     },
//                 }
//             );

//             setProjectList(response.data.suggestionlist);
//             console.log(projectList);

//         } catch (err) {
//             console.error("Error fetching category:", err);
//         } finally {
//         }
//     };

//     useEffect(() => {
//         getProject();
//     }, []);

//     const getCountry = async () => {
//         try {
//             const response = await axios.get(
//                 adminUrl + `get_country`,
//                 {
//                     headers: {
//                         "Content-Type": "application/json; charset=UTF-8",
//                         Accept: "application/json",
//                         "Access-Control-Allow-Origin": "*",
//                         Authorization:
//                             getCookie("mawalog"),
//                     },
//                 }
//             );

//             setCountryList(response.data.suggestionlist);

//         } catch (err) {
//             console.error("Error fetching category:", err);
//         } finally {
//         }
//     };

//     useEffect(() => {
//         getCountry();
//     }, []);

//     const getState = async () => {
//         try {
//             const response = await axios.get(
//                 adminUrl + `get_state`,
//                 {
//                     headers: {
//                         "Content-Type": "application/json; charset=UTF-8",
//                         Accept: "application/json",
//                         "Access-Control-Allow-Origin": "*",
//                         Authorization:
//                             getCookie("mawalog"),
//                     },
//                 }
//             );

//             setStateList(response.data.suggestionlist);

//         } catch (err) {
//             console.error("Error fetching category:", err);
//         } finally {
//         }
//     };

//     useEffect(() => {
//         getState();
//     }, []);

//     const getCity = async () => {
//         try {
//             const response = await axios.get(
//                 adminUrl + `get_city`,
//                 {
//                     headers: {
//                         "Content-Type": "application/json; charset=UTF-8",
//                         Accept: "application/json",
//                         "Access-Control-Allow-Origin": "*",
//                         Authorization:
//                             getCookie("mawalog"),
//                     },
//                 }
//             );

//             setCityList(response.data.suggestionlist);

//         } catch (err) {
//             console.error("Error fetching category:", err);
//         } finally {
//         }
//     };

//     useEffect(() => {
//         getCity();
//     }, []);

//     return (
//         <Container fluid style={{ width: '100%', padding: '0px' }}>
//             <div className="d-flex side3">
//                 <div className="d-none d-lg-block">
//                     <Sidebar selectedTab={"Project"} />
//                 </div>
//                 <Row sm={12} style={{ "--bs-gutter-x": "0px", margin: '0px', width: '100%', height: '100vh', overflow: 'auto', scrollbarWidth: "none", msOverflowStyle: "none", justifyContent: 'center', }}>
//                     <Col>
//                         <div className="top3">
//                             <Topbar selectedTab={"Project"} />
//                         </div>
//                         <div style={{ padding: "16px", fontFamily: 'Satoshi' }}>
//                             <div style={{ paddingBottom: "16px", gap: "10px", backgroundColor: "#F3F6F9", }}>
//                                 <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", }} className="mb-3">
//                                     <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "12px", }}>
//                                         <Link to='/client/list'>
//                                             <div style={{ borderRadius: "100px" }}>
//                                                 <img src={backarrow} />
//                                             </div>
//                                         </Link>
//                                         <div>
//                                             <span style={{ fontFamily: "Satoshi", fontWeight: "700", fontSize: "16px", lineHeight: "21.6px", color: "#212129", }}>
//                                                 New Client
//                                             </span>
//                                         </div>
//                                     </div>

//                                     <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "12px", }}>
//                                         <div style={{ borderRadius: "100px", border: "0.6px solid #E5E5E5", padding: "10px 28px 10px 28px", backgroundColor: "#FFFFFF", }}>
//                                             <span style={{ fontFamily: "Satoshi", fontWeight: "700", fontSize: "14px", lineHeight: "18.9px", color: "#21272C", }}>
//                                                 Cancel
//                                             </span>
//                                         </div>

//                                         <div style={{ borderRadius: "100px", border: "0.6px solid #E5E5E5", padding: "10px 28px 10px 28px", backgroundColor: "#3253EB", cursor: 'pointer', fontFamily: "Satoshi", fontWeight: "700", fontSize: "14px", lineHeight: "18.9px", color: "#FFFFFF", }} onClick={clientSave} >
//                                             Save
//                                         </div>
//                                     </div>
//                                 </div>

//                                 <div className="px-4 py-4 d-flex flex-column" style={{ borderRadius: '16px', gap: '24px', backgroundColor: '#FFFFFF' }}>
//                                     <div className="d-flex flex-column mb-4" style={{ gap: '8px' }}>
//                                         <div style={{ fontSize: '20px', fontWeight: '700', color: '#212129' }}>
//                                             Add Client
//                                         </div>
//                                         <div style={{ fontSize: '14px', fontWeight: '400', color: '#8790A1' }}>
//                                             Enter below details to create new client
//                                         </div>

//                                     </div>

//                                     <div className="d-flex flex-column mb-3" style={{ borderBottom: '1px dashed #E5E5E5' }}>
//                                         <div className="d-flex mb-4 align-items-center" style={{ gap: '16px' }}>

//                                             <div className="d-flex flex-column col-5" style={{ gap: '16px' }}>
//                                                 <div style={{ fontSize: '13px', fontWeight: '500', color: '#212129' }}>
//                                                     Code
//                                                 </div>
//                                                 <div style={{ width: '100%' }} className="d-flex flex-column">
//                                                     <input type="text" placeholder="Enter code" style={{ borderRadius: '10px', border: '0.7px solid #E5E5E5', backgroundColor: '#FFFFFF', width: '100%', fontWeight: '500', fontSize: '14px', color: '#8790A1' }} className="px-2 py-2" value={code} onChange={(event) => { setCode(event.target.value); }}
//                                                     />
//                                                     <div style={{ fontSize: '12px', fontWeight: '500', color: '#FF4444' }} className="mt-1">
//                                                         {validations.code}
//                                                     </div>
//                                                 </div>

//                                             </div>

//                                             <div className="d-flex flex-column col-5" style={{ gap: '16px' }}>
//                                                 <div style={{ fontSize: '13px', fontWeight: '500', color: '#212129' }}>
//                                                     Client name
//                                                 </div>
//                                                 <div style={{ width: '100%' }} className="d-flex flex-column">
//                                                     <input type="text" value={name} onChange={(event) => { setName(event.target.value); }}
//                                                         placeholder="Enter name" style={{ borderRadius: '10px', border: '0.7px solid #E5E5E5', backgroundColor: '#FFFFFF', width: '100%', fontWeight: '500', fontSize: '14px', color: '#8790A1' }} className="px-2 py-2" />
//                                                     <div style={{ fontSize: '12px', fontWeight: '500', color: '#FF4444' }} className="mt-1">
//                                                         {validations.name}
//                                                     </div>
//                                                 </div>

//                                             </div>

//                                         </div>

//                                         <div className="d-flex align-items-center mb-4" style={{ gap: '16px' }}>
//                                             <div className="d-flex flex-column col-5" style={{ gap: '16px' }}>
//                                                 <div style={{ fontSize: '13px', fontWeight: '500', color: '#212129' }}>
//                                                     Phone number
//                                                 </div>
//                                                 <div style={{ width: '100%' }} className="d-flex flex-column">
//                                                     <input type="text" value={phoneNumber} onChange={(event) => { setPhoneNumber(event.target.value); }}
//                                                         placeholder="Enter phone number" style={{ borderRadius: '10px', border: '0.7px solid #E5E5E5', backgroundColor: '#FFFFFF', width: '100%', fontWeight: '500', fontSize: '14px', color: '#8790A1' }} className="px-2 py-2" />
//                                                     <div style={{ fontSize: '12px', fontWeight: '500', color: '#FF4444' }} className="mt-1">
//                                                         {validations.phonenumber}
//                                                     </div>
//                                                 </div>

//                                             </div>

//                                             <div className="d-flex flex-column col-5" style={{ gap: '16px' }}>
//                                                 <div style={{ fontSize: '13px', fontWeight: '500', color: '#212129' }}>
//                                                     Email address
//                                                 </div>
//                                                 <div style={{ width: '100%' }}>
//                                                     <div style={{ width: '100%' }} className="d-flex flex-column">
//                                                         <input type="text" value={email} onChange={(event) => { setEmail(event.target.value); }}
//                                                             placeholder="Enter email" style={{ borderRadius: '10px', border: '0.7px solid #E5E5E5', backgroundColor: '#FFFFFF', width: '100%', fontWeight: '500', fontSize: '14px', color: '#8790A1' }} className="px-2 py-2" />
//                                                         <div style={{ fontSize: '12px', fontWeight: '500', color: '#FF4444' }} className="mt-1">
//                                                             {validations.email}
//                                                         </div>
//                                                     </div>
//                                                 </div>

//                                             </div>

//                                         </div>
//                                         <div className="d-flex align-items-center mb-5" style={{ gap: '16px' }}>

//                                             <div className="d-flex flex-column col-5" style={{ gap: '16px' }}>
//                                                 <div style={{ fontSize: '13px', fontWeight: '500', color: '#212129' }}>
//                                                     Project1
//                                                 </div>
//                                                 <div style={{ width: '100%' }}>
//                                                     <MultiSelectDropDown onDropdownChange={handleProjectDropdownChange} apiUrl={apiUrl + "get_project"} placeholder="Projects" selectedOption={selectedProjectOptions} />
//                                                 </div>

//                                             </div>

//                                         </div>

//                                     </div>

//                                     <div className="col-5">
//                                         <div style={{ fontSize: '15px', fontWeight: '700', color: '#212129' }} className="mb-4">
//                                             Address info
//                                         </div>

//                                         <div className="d-flex flex-column" style={{ gap: '8px' }} >
//                                             <div style={{ fontSize: '13px', fontWeight: '500', color: '#212129' }} className="">
//                                                 Address
//                                             </div>
//                                             <div style={{ width: '100%' }}>
//                                                 <div style={{ width: '100%' }} className="d-flex flex-column">
//                                                     <input type="text" value={address} onChange={(event) => { setAddress(event.target.value); }}
//                                                         placeholder="Enter address" style={{ borderRadius: '10px', border: '0.7px solid #E5E5E5', backgroundColor: '#FFFFFF', width: '100%', fontWeight: '500', fontSize: '14px', color: '#8790A1' }} className="px-2 py-2" />
//                                                     <div style={{ fontSize: '12px', fontWeight: '500', color: '#FF4444' }} className="mt-1">
//                                                         {validations.address}
//                                                     </div>
//                                                 </div>
//                                             </div>

//                                             <div className="d-flex align-items-center justify-content-between mt-3" style={{ gap: '16px' }}>
//                                                 <div className="d-flex flex-column col-5" style={{ gap: '16px' }}>
//                                                     <div style={{ fontSize: '13px', fontWeight: '500', color: '#212129' }}>
//                                                         City
//                                                     </div>
//                                                     <div style={{ width: '100%' }} className="d-flex flex-column">
//                                                         <select style={{ borderRadius: '10px', border: '0.7px solid #E5E5E5', backgroundColor: '#FFFFFF', width: '100%', fontWeight: '500', fontSize: '14px', color: '#8790A1' }} className="px-2 py-2" value={selectedCity} onChange={(e) => setSelectedCity(e.target.value)}>
//                                                             <option value="">Select</option>
//                                                             {cityList.length > 0 ? (
//                                                                 cityList.map((city, index) => (
//                                                                     <option key={index} value={city.value}>
//                                                                         {city.label}
//                                                                     </option>
//                                                                 ))
//                                                             ) : (
//                                                                 <option disabled>Loading cities...</option>
//                                                             )}

//                                                         </select>
//                                                         <div style={{ fontSize: '12px', fontWeight: '500', color: '#FF4444' }} className="mt-1">
//                                                             {validations.city_id}
//                                                         </div>
//                                                     </div>

//                                                 </div>

//                                                 <div className="d-flex flex-column col-5" style={{ gap: '16px' }}>
//                                                     <div style={{ fontSize: '13px', fontWeight: '500', color: '#212129' }}>
//                                                         State
//                                                     </div>
//                                                     <div style={{ width: '100%' }} className="d-flex flex-column">
//                                                         <select style={{ borderRadius: '10px', border: '0.7px solid #E5E5E5', backgroundColor: '#FFFFFF', width: '100%', fontWeight: '500', fontSize: '14px', color: '#8790A1' }} className="px-2 py-2" value={selectedState} onChange={(e) => setSelectedState(e.target.value)}>
//                                                             <option value="">Select</option>
//                                                             {stateList.length > 0 ? (
//                                                                 stateList.map((state, index) => (
//                                                                     <option key={index} value={state.value}>
//                                                                         {state.label}
//                                                                     </option>
//                                                                 ))
//                                                             ) : (
//                                                                 <option disabled>Loading states...</option>
//                                                             )}
//                                                         </select>
//                                                         <div style={{ fontSize: '12px', fontWeight: '500', color: '#FF4444' }} className="mt-1">
//                                                             {validations.state_id}
//                                                         </div>
//                                                     </div>

//                                                 </div>

//                                             </div>

//                                             <div className="d-flex align-items-center justify-content-between mt-3" style={{ gap: '16px' }}>
//                                                 <div className="d-flex flex-column col-5" style={{ gap: '16px' }}>
//                                                     <div style={{ fontSize: '13px', fontWeight: '500', color: '#212129' }}>
//                                                         Country
//                                                     </div>
//                                                     <div style={{ width: '100%' }} className="d-flex flex-column">
//                                                         <select style={{ borderRadius: '10px', border: '0.7px solid #E5E5E5', backgroundColor: '#FFFFFF', width: '100%', fontWeight: '500', fontSize: '14px', color: '#8790A1' }} className="px-2 py-2" value={selectedCountry} onChange={(e) => setSelectedCountry(e.target.value)}>
//                                                             <option value="">Select</option>
//                                                             {countryList.length > 0 ? (
//                                                                 countryList.map((country, index) => (
//                                                                     <option key={index} value={country.value}>
//                                                                         {country.label}
//                                                                     </option>
//                                                                 ))
//                                                             ) : (
//                                                                 <option disabled>Loading countries...</option>
//                                                             )}
//                                                         </select>
//                                                         <div style={{ fontSize: '12px', fontWeight: '500', color: '#FF4444' }} className="mt-1">
//                                                             {validations.country_id}
//                                                         </div>
//                                                     </div>

//                                                 </div>

//                                                 <div className="d-flex flex-column col-5" style={{ gap: '16px' }}>
//                                                     <div style={{ fontSize: '13px', fontWeight: '500', color: '#212129' }}>
//                                                         Zip code
//                                                     </div>
//                                                     <div style={{ width: '100%' }}>
//                                                         <div style={{ width: '100%' }} className="d-flex flex-column">
//                                                             <input type="text" placeholder="Enter zip code" style={{ borderRadius: '10px', border: '0.7px solid #E5E5E5', backgroundColor: '#FFFFFF', width: '100%', fontWeight: '500', fontSize: '14px', color: '#8790A1' }} className="px-2 py-2" />
//                                                             <div style={{ fontSize: '12px', fontWeight: '500', color: '#FF4444' }} className="mt-1">
//                                                                 { }
//                                                             </div>
//                                                         </div>
//                                                     </div>

//                                                 </div>

//                                             </div>

//                                         </div>

//                                     </div>

//                                 </div>

//                             </div>

//                         </div>
//                     </Col>

//                 </Row>
//             </div>

//         </Container>
//     );
// };

// export default Clientadd;
